import { createContext, useContext, type ReactNode } from 'react'
import type { PipelineId } from '@ludus/core'

/**
 * Carries the active pipeline id from the `/pipelines/:pipelineId` route
 * boundary down to the data layer. Before SBO-59 the id was a module constant
 * (`ACTIVE_PIPELINE_ID` off `VITE_PIPELINE_ID`); now it flows from the URL.
 *
 * The point of a context (rather than threading `pipelineId` through every
 * selector) is that `usePipelineStages` / `usePipelineManagers` / … keep their
 * no-arg signatures — only `usePipelinesSourceState` reads the id, and the
 * four SWR keys it builds become per-pipeline automatically.
 */
const PipelineIdContext = createContext<PipelineId | null>(null)

export function PipelineIdProvider({
  pipelineId,
  children,
}: {
  pipelineId: PipelineId
  children: ReactNode
}) {
  return <PipelineIdContext.Provider value={pipelineId}>{children}</PipelineIdContext.Provider>
}

export function usePipelineId(): PipelineId {
  const id = useContext(PipelineIdContext)
  if (!id) {
    throw new Error(
      'usePipelineId: no pipeline id in context. Render under <PipelineIdProvider>, mounted at the /pipelines/:pipelineId route boundary.',
    )
  }
  return id
}
